import React from 'react';
import type { CreativeSlotState } from '../../../types/selfService';
import {
  rederiveSegmentSharesOnDateChange,
  reseatSharesAfterRemoval,
  resetToEven,
  seedSegmentSharesForNewCreative,
  singleSegmentShareForWindow,
  type CreativeWindow,
} from '../shared/rotationSegments';

interface CreativeMeta {
  storagePath: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

type UploadFn = (file: File) => Promise<CreativeMeta>;

export interface LibraryPick {
  storagePath: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  clickThroughUrl: string;
}

export interface UseCreativeSlotsResult {
  /** All commercial slots, including slot 0. Always has at least one entry. */
  slots: CreativeSlotState[];
  /** Local-only File reference for each slot. Null when uploaded or empty. */
  files: Record<string, File | null>;
  /** Per-slot upload state for the inline "Uploading…" / error UI. */
  uploadState: Record<string, { uploading: boolean; error: string | null }>;
  /** Append a new empty slot. Returns the new slot's id. */
  addSlot: () => string;
  removeSlot: (slotId: string) => void;
  updateSlot: (slotId: string, patch: Partial<CreativeSlotState>) => void;
  /** Reset every overlap Rotation Segment to even split; solo stays 100%. */
  resetWeights: () => void;
  handleFilePick: (slotId: string, file: File | null) => void;
  /** Apply a library pick to a slot (no upload — storagePath is already set). */
  applyLibraryPick: (slotId: string, pick: LibraryPick) => void;
  /** Wait for any in-flight uploads and return the final slot states. */
  awaitUploads: () => Promise<CreativeSlotState[]>;
  /** Replace the entire slot set. Empty `initial` reseeds a single empty slot 0. */
  reset: (initial?: CreativeSlotState[]) => void;
}

function newSlotId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `slot-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function newEmptySlot(start: string, end: string): CreativeSlotState {
  // CTV-476: when the campaign window hasn't been chosen yet, seed
  // segmentShares as an empty array rather than a malformed
  // [{ "", "", 100 }]. The single-creative sync effect populates the
  // array as soon as the buyer picks dates on step 2.
  const segmentShares = start && end ? singleSegmentShareForWindow(start, end) : [];
  return {
    slotId: newSlotId(),
    storagePath: null,
    filename: '',
    mimeType: 'video/mp4',
    sizeBytes: 0,
    clickThroughUrl: '',
    segmentShares,
    startDate: start,
    endDate: end,
  };
}

// Translate a CreativeSlotState[] to the CreativeWindow[] shape the shared
// rotation-segments helpers expect (their `id` is the slot's stable id).
function toRotationWindows(slots: CreativeSlotState[]): CreativeWindow[] {
  return slots.map((s) => ({
    id: s.slotId,
    startDate: s.startDate,
    endDate: s.endDate,
    segmentShares: s.segmentShares.map((seg) => ({ ...seg })),
  }));
}

// Merge re-derived segmentShares back into the slot list, leaving every other
// field untouched.
function applyDerivedShares(
  slots: CreativeSlotState[],
  derived: CreativeWindow[],
): CreativeSlotState[] {
  const byId = new Map(derived.map((d) => [d.id, d]));
  return slots.map((s) => {
    const d = byId.get(s.slotId);
    if (!d) return s;
    return {
      ...s,
      startDate: d.startDate,
      endDate: d.endDate,
      segmentShares: d.segmentShares.map((seg) => ({ ...seg })),
    };
  });
}

/**
 * Normalize a list of slots so every Rotation Segment's eligible shares sum
 * to 100, preserving each slot's date window. Bails for 0- or 1-slot inputs
 * (a single slot is always at 100% in its own segment — `resetToEven` would
 * still yield the same result, but the early return makes the no-op cheap
 * and the intent explicit).
 *
 * Defense for hydration paths that build slots from a payload — e.g. the
 * "Run this Campaign Again" rerun strips dates/shares and the wizard
 * defaults each slot to `singleSegmentShareForWindow(...)` (share=100). With
 * N≥2 slots on the same window that sums to 100·N and the server's
 * `assertSegmentSharesValid` rejects the submission at payment. Folding the
 * prefilled slot list through this helper before `reset(...)` re-derives
 * even splits in every overlap segment.
 */
export function equalizeSharesAcrossSlots(slots: CreativeSlotState[]): CreativeSlotState[] {
  if (slots.length < 2) return slots;
  const windows = toRotationWindows(slots);
  const normalized = resetToEven(windows);
  return applyDerivedShares(slots, normalized);
}

/**
 * CTV-466 follow-up + CTV-476 — unified state for every Creative slot in the
 * wizard (slots 0..N). Slot 0 lives at slots[0]; it has no privileged status.
 * The scheduler reads from this same array.
 *
 * Files and upload promises live in refs (not state) — re-renders during
 * upload would clobber the in-flight Promise. UI feedback (uploading flag,
 * error) lives in state so the slot card can render the spinner.
 */
export function useCreativeSlots(args: {
  campaignStartDate: string;
  campaignEndDate: string;
  uploadFile: UploadFn;
}): UseCreativeSlotsResult {
  const { campaignStartDate, campaignEndDate, uploadFile } = args;
  const [slots, setSlots] = React.useState<CreativeSlotState[]>(() => [
    newEmptySlot(campaignStartDate, campaignEndDate),
  ]);
  const [files, setFiles] = React.useState<Record<string, File | null>>({});
  const [uploadState, setUploadState] = React.useState<
    Record<string, { uploading: boolean; error: string | null }>
  >({});
  const promisesRef = React.useRef<Map<string, Promise<CreativeMeta>>>(new Map());

  // CTV-476: when there's exactly one creative on the campaign, force its
  // window to match the campaign window and reseed segmentShares to a
  // single 100% element. The hook is mounted before the buyer picks dates
  // on step 2 — without this sync, slot[0]'s startDate/endDate stay at the
  // initial-render value (empty strings) and step 3 fires the ADR-0006
  // coverage banner for what the buyer obviously meant as "run my one
  // commercial the whole campaign." Also handles the multi → single
  // transition: when a removal drops the count to 1, the survivor widens.
  React.useEffect(() => {
    if (!campaignStartDate || !campaignEndDate) return;
    setSlots((prev) => {
      if (prev.length !== 1) return prev;
      const only = prev[0];
      const target = singleSegmentShareForWindow(campaignStartDate, campaignEndDate);
      const alreadyMatches =
        only.startDate === campaignStartDate &&
        only.endDate === campaignEndDate &&
        only.segmentShares.length === 1 &&
        only.segmentShares[0].startDate === campaignStartDate &&
        only.segmentShares[0].endDate === campaignEndDate &&
        only.segmentShares[0].share === 100;
      if (alreadyMatches) return prev;
      return [
        {
          ...only,
          startDate: campaignStartDate,
          endDate: campaignEndDate,
          segmentShares: target,
        },
      ];
    });
  }, [campaignStartDate, campaignEndDate, slots.length]);

  const addSlot = React.useCallback((): string => {
    const slotId = newSlotId();
    setSlots((prev) => {
      const newSlot: CreativeSlotState = {
        slotId,
        storagePath: null,
        filename: '',
        mimeType: 'video/mp4',
        sizeBytes: 0,
        clickThroughUrl: '',
        segmentShares: singleSegmentShareForWindow(campaignStartDate, campaignEndDate),
        startDate: campaignStartDate,
        endDate: campaignEndDate,
      };
      const prospective = [...prev, newSlot];
      const derived = seedSegmentSharesForNewCreative(toRotationWindows(prev), {
        id: slotId,
        startDate: campaignStartDate,
        endDate: campaignEndDate,
      });
      return applyDerivedShares(prospective, derived);
    });
    return slotId;
  }, [campaignStartDate, campaignEndDate]);

  const removeSlot = React.useCallback((slotId: string) => {
    setSlots((prev) => {
      const next = prev.filter((s) => s.slotId !== slotId);
      if (next.length === 0) return next;
      // CTV-476: when a removal drops the count to 1, widen the survivor
      // to the full campaign window with a single 100% segment. Without
      // this, removing the "wide" creative from a multi-creative setup
      // would leave the survivor with whatever narrower window the buyer
      // had given it and re-trigger the coverage banner — not the
      // obvious intent of "this is my only ad now."
      if (next.length === 1 && campaignStartDate && campaignEndDate) {
        return [
          {
            ...next[0],
            startDate: campaignStartDate,
            endDate: campaignEndDate,
            segmentShares: singleSegmentShareForWindow(campaignStartDate, campaignEndDate),
          },
        ];
      }
      const derived = reseatSharesAfterRemoval(toRotationWindows(next));
      return applyDerivedShares(next, derived);
    });
    setFiles((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
    setUploadState((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
    promisesRef.current.delete(slotId);
  }, [campaignStartDate, campaignEndDate]);

  const updateSlot = React.useCallback((slotId: string, patch: Partial<CreativeSlotState>) => {
    setSlots((prev) => {
      // CTV-476: when the patch changes the date window, re-derive sibling
      // segment shares per the Re-derivation rules (carry / default-even /
      // re-normalize). Non-date patches (filename, clickThroughUrl, etc.)
      // bypass the helper so they don't churn shares.
      const willChangeDates =
        (patch.startDate !== undefined && patch.startDate !== prev.find((s) => s.slotId === slotId)?.startDate) ||
        (patch.endDate !== undefined && patch.endDate !== prev.find((s) => s.slotId === slotId)?.endDate);
      const patched = prev.map((s) => (s.slotId === slotId ? { ...s, ...patch } : s));
      if (!willChangeDates) return patched;
      const target = patched.find((s) => s.slotId === slotId);
      if (!target) return patched;
      const derived = rederiveSegmentSharesOnDateChange(
        toRotationWindows(prev),
        slotId,
        target.startDate,
        target.endDate,
      );
      return applyDerivedShares(patched, derived);
    });
  }, []);

  const resetWeights = React.useCallback(() => {
    setSlots((prev) => applyDerivedShares(prev, resetToEven(toRotationWindows(prev))));
  }, []);

  const handleFilePick = React.useCallback(
    (slotId: string, file: File | null) => {
      setFiles((prev) => ({ ...prev, [slotId]: file }));
      setUploadState((prev) => ({ ...prev, [slotId]: { uploading: false, error: null } }));
      if (!file) {
        setSlots((prev) =>
          prev.map((s) => (s.slotId === slotId ? { ...s, storagePath: null, filename: '', sizeBytes: 0 } : s)),
        );
        promisesRef.current.delete(slotId);
        return;
      }
      setSlots((prev) =>
        prev.map((s) =>
          s.slotId === slotId
            ? {
                ...s,
                storagePath: null,
                filename: file.name,
                mimeType: file.type || 'video/mp4',
                sizeBytes: file.size,
                rejected: false,
                rejectionReason: null,
                rejectionReasonCodes: null,
              }
            : s,
        ),
      );
      setUploadState((prev) => ({ ...prev, [slotId]: { uploading: true, error: null } }));
      const promise = uploadFile(file)
        .then((meta) => {
          setSlots((prev) =>
            prev.map((s) =>
              s.slotId === slotId
                ? {
                    ...s,
                    storagePath: meta.storagePath,
                    filename: meta.filename,
                    mimeType: meta.mimeType,
                    sizeBytes: meta.sizeBytes,
                  }
                : s,
            ),
          );
          setUploadState((prev) => ({ ...prev, [slotId]: { uploading: false, error: null } }));
          return meta;
        })
        .catch((e: unknown) => {
          const msg = e instanceof Error ? e.message : 'Video upload failed';
          setUploadState((prev) => ({ ...prev, [slotId]: { uploading: false, error: msg } }));
          promisesRef.current.delete(slotId);
          throw e;
        });
      promisesRef.current.set(slotId, promise);
    },
    [uploadFile],
  );

  const applyLibraryPick = React.useCallback((slotId: string, pick: LibraryPick) => {
    setFiles((prev) => ({ ...prev, [slotId]: null }));
    setUploadState((prev) => ({ ...prev, [slotId]: { uploading: false, error: null } }));
    promisesRef.current.delete(slotId);
    setSlots((prev) =>
      prev.map((s) =>
        s.slotId === slotId
          ? {
              ...s,
              storagePath: pick.storagePath,
              filename: pick.filename,
              mimeType: pick.mimeType,
              sizeBytes: pick.sizeBytes,
              clickThroughUrl: pick.clickThroughUrl,
              rejected: false,
              rejectionReason: null,
              rejectionReasonCodes: null,
            }
          : s,
      ),
    );
  }, []);

  const awaitUploads = React.useCallback(async () => {
    const entries = Array.from(promisesRef.current.entries());
    await Promise.all(entries.map(([, p]) => p.catch(() => null)));
    return new Promise<CreativeSlotState[]>((resolve) => {
      setSlots((current) => {
        resolve(current);
        return current;
      });
    });
  }, []);

  const reset = React.useCallback(
    (initial: CreativeSlotState[] = []) => {
      setSlots(initial.length > 0 ? initial : [newEmptySlot(campaignStartDate, campaignEndDate)]);
      setFiles({});
      setUploadState({});
      promisesRef.current.clear();
    },
    [campaignStartDate, campaignEndDate],
  );

  return {
    slots,
    files,
    uploadState,
    addSlot,
    removeSlot,
    updateSlot,
    resetWeights,
    handleFilePick,
    applyLibraryPick,
    awaitUploads,
    reset,
  };
}
